import { CyclicalDependencyError } from "../errors";
import { typeValidationMap } from "../utils/type-validation";
import { extractDependencies } from "./extract-dependencies";
function* createExpressionsFromTerm(term, iterable) {
    var _a;
    if (term.calculate) {
        if (term.calculate.type !== "syntax-tree") {
            throw new Error(`There is no syntax for the calculate expression for term "${term.name}"`);
        }
        const exp = {
            type: "basic",
            name: "calculate",
            termName: term.name,
            iterableName: iterable === null || iterable === void 0 ? void 0 : iterable.name,
            syntax: term.calculate.tree,
            dependencies: extractDependencies(term.calculate.tree, {
                term,
                iterable,
            }),
        };
        yield exp;
    }
    if (term.label && term.label.type !== "text") {
        if (term.label.type !== "syntax-tree") {
            throw new Error(`There is no syntax for the label expression for term "${term.name}"`);
        }
        const exp = {
            type: "basic",
            name: "label",
            termName: term.name,
            iterableName: iterable === null || iterable === void 0 ? void 0 : iterable.name,
            syntax: term.label.tree,
            dependencies: extractDependencies(term.label.tree, { term, iterable }),
        };
        yield exp;
    }
    if (term.relevant) {
        if (term.relevant.type !== "syntax-tree") {
            throw new Error(`There is no syntax for the relevant expression for term "${term.name}"`);
        }
        const exp = {
            type: "basic",
            name: "relevant",
            termName: term.name,
            iterableName: iterable === null || iterable === void 0 ? void 0 : iterable.name,
            syntax: term.relevant.tree,
            dependencies: extractDependencies(term.relevant.tree, { term, iterable }),
        };
        yield exp;
    }
    if (term.required) {
        if (term.required.type !== "syntax-tree") {
            throw new Error(`There is no syntax for the required expression for term "${term.name}"`);
        }
        const exp = {
            type: "basic",
            name: "required",
            termName: term.name,
            iterableName: iterable === null || iterable === void 0 ? void 0 : iterable.name,
            syntax: term.required.tree,
            dependencies: extractDependencies(term.required.tree, { term, iterable }),
        };
        // A term can only be required if it is relevant
        // so relevant is always a dependency of required if it exists
        if (term.relevant) {
            exp.dependencies.push(iterable
                ? {
                    type: "iterable-term",
                    iterableName: iterable.name,
                    termName: term.name,
                    property: "isRelevant",
                }
                : {
                    type: "term",
                    termName: term.name,
                    property: "isRelevant",
                });
        }
        yield exp;
    }
    if (term.readonly) {
        if (term.readonly.type !== "syntax-tree") {
            throw new Error(`There is no syntax for the readonly expression for term "${term.name}"`);
        }
        const exp = {
            type: "basic",
            name: "readonly",
            termName: term.name,
            iterableName: iterable === null || iterable === void 0 ? void 0 : iterable.name,
            syntax: term.readonly.tree,
            dependencies: extractDependencies(term.readonly.tree, { term, iterable }),
        };
        yield exp;
    }
    if (term.validationList ||
        term.enums ||
        (term.valueType &&
            (typeValidationMap[term.valueType] || term.valueType === "date"))) {
        const exp = {
            type: "validation",
            termName: term.name,
            iterableName: iterable === null || iterable === void 0 ? void 0 : iterable.name,
            validators: [],
            valueType: term.valueType || "string",
            dependencies: [
                iterable
                    ? {
                        type: "iterable-term",
                        iterableName: iterable.name,
                        termName: term.name,
                        property: "value",
                    }
                    : {
                        type: "term",
                        termName: term.name,
                        property: "value",
                    },
                iterable
                    ? {
                        type: "iterable-term",
                        iterableName: iterable.name,
                        termName: term.name,
                        property: "enums",
                    }
                    : {
                        type: "term",
                        termName: term.name,
                        property: "enums",
                    },
            ],
        };
        if (term.validationList) {
            for (const validation of term.validationList) {
                if (validation.min) {
                    if (validation.min.type === "syntax") {
                        throw new Error(`There is no syntax-tree for the validation value expression for term "${term.name}"`);
                    }
                    exp.validators.push({
                        type: mapValidatorTypes(validation.validationType, "min"),
                        syntax: validation.min.type === "text"
                            ? {
                                type: "string",
                                value: validation.min.value,
                                text: validation.min.value,
                            }
                            : validation.min.tree,
                    });
                    if (validation.min.type === "syntax-tree") {
                        exp.dependencies.push(...extractDependencies(validation.min.tree, { term, iterable }));
                    }
                }
                if (validation.max) {
                    if (validation.max.type === "syntax") {
                        throw new Error(`There is no syntax-tree for the validation value expression for term "${term.name}"`);
                    }
                    exp.validators.push({
                        type: mapValidatorTypes(validation.validationType, "max"),
                        syntax: validation.max.type === "text"
                            ? {
                                type: "string",
                                value: validation.max.value,
                                text: validation.max.value,
                            }
                            : validation.max.tree,
                    });
                    if (validation.max.type === "syntax-tree") {
                        exp.dependencies.push(...extractDependencies(validation.max.tree, { term, iterable }));
                    }
                }
            }
        }
        yield exp;
    }
    if ((_a = term.enums) === null || _a === void 0 ? void 0 : _a.some(({ relevant }) => relevant)) {
        const exp = {
            type: "enums-relevant",
            termName: term.name,
            iterableName: iterable === null || iterable === void 0 ? void 0 : iterable.name,
            items: [],
            dependencies: [],
        };
        for (const [index, enumItem] of term.enums.entries()) {
            if (enumItem.relevant) {
                if (enumItem.relevant.type !== "syntax-tree") {
                    throw new Error(`There is no syntax for the enum relevant expression for term "${term.name}"`);
                }
                exp.items.push({
                    syntax: enumItem.relevant.tree,
                    enumIndex: index,
                });
                exp.dependencies.push(...extractDependencies(enumItem.relevant.tree, {
                    term,
                    iterable,
                }));
            }
        }
        yield exp;
    }
}
export function listReactiveExpressions(formModel) {
    const list = [];
    const reference = {
        terms: {},
        iterables: {},
    };
    for (const term of Object.values(formModel.terms)) {
        for (const exp of createExpressionsFromTerm(term)) {
            throwIfSelfReferencingDependencies(exp);
            if (!reference.terms[term.name]) {
                reference.terms[term.name] = {};
            }
            if (exp.type === "basic") {
                reference.terms[term.name][exp.name] = exp;
            }
            if (exp.type === "validation") {
                reference.terms[term.name].validation = exp;
            }
            if (exp.type === "enums-relevant") {
                reference.terms[term.name].enumsRelevant = exp;
            }
            list.push(exp);
        }
    }
    for (const iterable of Object.values(formModel.iterables)) {
        if (!reference.iterables[iterable.name]) {
            if (iterable.count.type !== "syntax-tree") {
                throw new Error(`There is no syntax for the count expression for iterable "${iterable.name}"`);
            }
            const countExp = {
                type: "count",
                iterableName: iterable.name,
                syntax: iterable.count.tree,
                dependencies: extractDependencies(iterable.count.tree, { iterable }),
            };
            throwIfSelfReferencingDependencies(countExp);
            reference.iterables[iterable.name] = {
                count: countExp,
                terms: {},
            };
            list.push(countExp);
        }
        for (const term of Object.values(iterable.terms)) {
            for (const exp of createExpressionsFromTerm(term, iterable)) {
                throwIfSelfReferencingDependencies(exp);
                if (!reference.iterables[iterable.name].terms[term.name]) {
                    reference.iterables[iterable.name].terms[term.name] = {};
                }
                if (exp.type === "basic") {
                    reference.iterables[iterable.name].terms[term.name][exp.name] = exp;
                }
                if (exp.type === "validation") {
                    reference.iterables[iterable.name].terms[term.name].validation = exp;
                }
                if (exp.type === "enums-relevant") {
                    reference.iterables[iterable.name].terms[term.name].enumsRelevant =
                        exp;
                }
                list.push(exp);
            }
        }
    }
    return [list, reference];
}
function mapValidatorTypes(validationType, minOrMax) {
    switch (validationType) {
        case "amount":
            if (minOrMax === "min") {
                return "amount-min";
            }
            if (minOrMax === "max") {
                return "amount-max";
            }
            throw new Error(`Unknown validation type "${validationType}" > "${minOrMax}"`);
        case "length":
            if (minOrMax === "min") {
                return "length-min";
            }
            if (minOrMax === "max") {
                return "length-max";
            }
            throw new Error(`Unknown validation type "${validationType}" > "${minOrMax}"`);
        case "date-range":
            if (minOrMax === "min") {
                return "date-range-min";
            }
            if (minOrMax === "max") {
                return "date-range-max";
            }
            throw new Error(`Unknown validation type "${validationType}" > "${minOrMax}"`);
        default:
            throw new Error(`Unknown validation type "${validationType}"`);
    }
}
function throwIfSelfReferencingDependencies(exp) {
    for (const dep of exp.dependencies) {
        if ((dep.type === "term" || dep.type === "iterable-term") &&
            (exp.type === "basic" || exp.type === "validation") &&
            dep.termName === exp.termName &&
            (dep.type !== "iterable-term" || dep.iterableName === exp.iterableName) &&
            ((exp.type === "basic" &&
                doesPropertyMatchExpName(dep.property, exp.name)) ||
                (exp.type === "validation" &&
                    (dep.property === "isValid" || dep.property === "isComplete")))) {
            throw new CyclicalDependencyError(`Self referencing dependency found for term "${exp.iterableName ? exp.iterableName + "/" : ""}${exp.termName}" in "${exp.type === "basic" ? exp.name : exp.type}"`, exp.type === "basic" ? exp.name : exp.type, exp.termName, exp.iterableName);
        }
        if (dep.type === "iterable" &&
            exp.type === "count" &&
            dep.iterableName === exp.iterableName &&
            dep.property === "count") {
            throw new CyclicalDependencyError(`Self referencing dependency found for iterable "${exp.iterableName}" in "count"`, "count", undefined, exp.iterableName);
        }
    }
}
function doesPropertyMatchExpName(property, expName) {
    switch (property) {
        case "value":
        case "ready":
        case "hasValue":
        case "isValid":
            return expName === "calculate";
        case "isComplete":
            return (expName === "calculate" ||
                expName === "relevant" ||
                expName === "required");
        case "label":
            return expName === "label";
        case "isRelevant":
            return expName === "relevant";
        case "isRequired":
            return expName === "required" || expName === "relevant";
        case "isReadonly":
            return expName === "readonly";
        default:
            throw new Error(`Unknown dependency with property "${property}" in expression "${expName}"`);
    }
}
//# sourceMappingURL=list-reactive-expressions.js.map